import { NoteText } from "@/components/notes";
import { UnitPicker } from "@/components/pickers/unit-picker";
import { ServiceFormUnitCalculatorWrapper } from "@/components/services/shared";
import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { PickServiceModal } from "@/modals/organizations/pick-service-modal";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { type ItemInstance, TREE_INDENT, type TreeItem, type TreeItemApi } from "@msuite/ui/dnd";
import { FormCheckbox, NumberInput, Textarea, TextInput } from "@msuite/ui/form";
import { useDisclosure, useLoading } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { IconTag, UnitTag } from "@msuite/ui/tag";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { calculateBlockTotal } from "@msuite/utils/calculations";
import { formatNumber, guardNumber } from "@msuite/utils/numbers";
import { getAtomicNumberString } from "@msuite/utils/organizations";
import type { CalculationFormSchema, CalculationFormServiceItem } from "@msuite/utils/schemas";
import { calculateService, serviceUnits } from "@msuite/utils/services";
import type { NullableFields, ServiceUnit, Tables } from "@msuite/utils/types";
import React, { Fragment, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CalculationFormItemFilesAndNotes } from "./calculation-form-item-files-and-notes";
import { CalculationFormItemPositions } from "./calculation-form-item-positions";
import { useCalculationFormContext } from "./context";
import { CalculationFormHandler } from "./shared";

/** Props Interface */
interface CalculationFormItemProps<T, U> {
	item: ItemInstance<TreeItem<CalculationFormServiceItem>>;
	api: TreeItemApi<T, U>;
}

export function CalculationFormItem<T, U>({ item, api }: CalculationFormItemProps<T, U>) {
	const { isEditing, setIsEditing } = useCalculationFormContext();
	const isEdit = isEditing.includes(item.getId());

	/** Context */
	const methods = useFormContext<CalculationFormSchema>();
	const { t } = useTranslation();
	const mentionOptions = useMentionOptions();
	const resetLoader = useLoading();
	const pickServiceModal = useDisclosure();

	const notesDisclosure = useDisclosure();
	const filesDisclosure = useDisclosure();
	const notesContainerRef = useRef<HTMLDivElement>(null);

	/** Computed */
	// The index is structural — resolve it non-reactively. Structural changes (add/remove/reorder)
	// already re-render this row through the tree's own data update, so subscribing to the whole
	// services array just to find the index would re-render every service on every keystroke.
	const allServices = methods.getValues("services");
	const index = new CalculationFormHandler(allServices).getItemIndex(item.getId());
	const isBlock = allServices?.[index]?.type === "block";
	// A leaf service subscribes only to its own slice, so typing in one service no longer re-renders
	// every other service. A block rolls up its children's totals, so it must watch the array.
	const watched = useWatch({
		control: methods.control,
		name: isBlock ? "services" : `services.${index}`,
	});
	const services = (isBlock ? watched : allServices) as CalculationFormServiceItem[] | undefined;
	const values = (isBlock ? services?.[index] : watched) as CalculationFormServiceItem | undefined;
	const blockTotal = isBlock && values ? calculateBlockTotal(values, services ?? []) : 0;
	const positionsTotal = !values || isBlock ? 0 : calculateService(values);

	/** Functions */
	function buildOrderNumber<T>(item: ItemInstance<T>, currentSegments = "") {
		let segments = currentSegments;
		const currentIndex = item.getIndexInParent() + 1;
		segments = segments ? `${currentIndex}.${segments}` : `${currentIndex}`;
		const parent = item.getParent();
		if (parent?.getId() === "root") return segments;
		if (parent) {
			return buildOrderNumber(parent, segments);
		}
		return segments;
	}

	function handleOnToggleIsEdit() {
		if (isEdit) {
			setIsEditing(isEditing.filter((id) => id !== item.getId()));
		} else {
			setIsEditing([...isEditing, item.getId()]);
		}
	}

	async function handleOnResetValues() {
		try {
			resetLoader.start();
			const serviceId = values?.organizationServiceId;
			if (!serviceId) throw new Error("Service ID is required");
			const response = await client.organizations.services[":serviceId"].$get({
				param: { serviceId },
			});
			const data = await responseHandler(response);
			if (!data.service) throw new Error("Service not found");
			const resetKeys: (keyof CalculationFormServiceItem)[] = [
				"unitPrice",
				"shortText",
				"longText",
			];
			for (const key of resetKeys) {
				// @ts-expect-error
				const value = data.service[key];
				if (value) {
					methods.setValue(`services.${index}.${key}`, value);
				}
			}
			setIsEditing(isEditing.filter((id) => id !== item.getId()));
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		} finally {
			resetLoader.stop();
		}
	}

	async function handleOnCompletePickService(
		service: NullableFields<Tables<"organizationServices">>,
	) {
		api.append(
			{ ...service, positions: [{ name: "", ranking: 0 }] } as U,
			item as unknown as ItemInstance<T>,
		);
	}

	function handleOnAddCustomService() {
		const customItem: NullableFields<CalculationFormServiceItem> = {
			shortText: "Eigene Leistung",
			positions: [{ name: "", ranking: 0 }],
		};
		api.append(customItem as U, item as unknown as ItemInstance<T>);
	}

	function handleOnDuplicate() {
		if (!isBlock) {
			api.onDuplicate();
			return;
		}
		const services = new CalculationFormHandler(methods.getValues("services")).duplicateBlock(
			item.getId(),
		);
		if (!services) return;
		methods.setValue("services", services);
	}

	/** Guard */
	if (!values) return null;

	/** Render */
	return (
		<React.Fragment>
			<ContentCard
				variant="plain"
				title={`${getAtomicNumberString(buildOrderNumber(item))}`}
				icon={
					<div
						{...api.dragHandleProps}
						className="cursor-grab active:cursor-grabbing"
					>
						<Icon name="GripVertical" />
					</div>
				}
				className="card"
				layout={false}
				menuItems={[
					{
						label: t("general.edit"),
						icon: () => <Icon name="Edit" />,
						onClick: handleOnToggleIsEdit,
					},
					"divider",
					...(isBlock
						? [
								{
									label: t("general.add"),
									icon: () => <Icon name="Plus" />,
									onClick: pickServiceModal.onOpen,
								},
								"divider" as const,
							]
						: []),
					{
						label: t("calculations.move_up"),
						icon: () => <Icon name="ArrowUp" />,
						onClick: api.moveUp,
						isDisabled: guardNumber(item.getIndexInParent()) === 0,
					},
					{
						label: t("calculations.move_down"),
						icon: () => <Icon name="ArrowDown" />,
						onClick: api.moveDown,
						isDisabled:
							guardNumber(item.getIndexInParent()) ===
							guardNumber(item.getParent()?.getChildren()?.length) - 1,
					},
					{
						label: t("calculations.remove_from_parent"),
						icon: () => <Icon name="ArrowLeftFromLine" />,
						onClick: api.unindent,
						isDisabled: item.getParent()?.getId() === item.getTree().getRootItem().getId(),
					},
					"divider",
					{
						label: t("general.duplicate"),
						icon: () => <Icon name="Copy" />,
						onClick: handleOnDuplicate,
					},
					...(isBlock
						? []
						: [
								{
									label: t("general.reset"),
									icon: () => <Icon name="RefreshCw" />,
									onClick: handleOnResetValues,
								},
							]),
					"divider",
					{
						label: t("general.delete"),
						icon: icon("Trash2"),
						isDanger: true,
						onClick: api.onRemove,
					},
				]}
			>
				{isEdit ? (
					<React.Fragment>
						<TextInput
							control={methods.control}
							name={`services.${index}.shortText`}
							label={t("organization.data.short_text")}
							placeholder={t("placeholders.short_text")}
							isRequired
						/>{" "}
						{!isBlock && (
							<Textarea
								control={methods.control}
								name={`services.${index}.longText`}
								label={t("organization.data.long_text")}
								placeholder={t("placeholders.long_text")}
								disableFormatting
								isRequired
								{...mentionOptions}
							/>
						)}
						<Divider variant="card" />
						<div className="h-stack w-full justify-end">
							{isBlock ? null : (
								<Button
									onClick={handleOnResetValues}
									isLoading={resetLoader.isLoading}
								>
									{t("general.reset")}
								</Button>
							)}
							<Button
								variant="primary"
								onClick={handleOnToggleIsEdit}
							>
								{t("general.done")}
							</Button>
						</div>
						<Divider variant="card" />
					</React.Fragment>
				) : (
					<div className="v-stack gap-0 card -mx-1.5">
						<div className="h-stack justify-between">
							<p>{values.shortText}</p>
							<Button
								icon={<Icon name="Edit" />}
								variant="plain"
								size="xxs"
								onClick={handleOnToggleIsEdit}
							/>
						</div>
						{values.longText ? (
							<Fragment>
								<Divider
									variant="card"
									className="my-2"
								/>
								<div className="max-w-full">
									<NoteText
										note={values.longText}
										withPopover
										lineClamp={3}
									/>
								</div>
							</Fragment>
						) : null}
						<Divider
							variant="card"
							className="my-2"
						/>
						{isBlock ? (
							<div className="h-stack">
								<IconTag
									iconName="Layers"
									color="accent"
								>
									{t("calculations.block")}
								</IconTag>
								<p>× {guardNumber(values.quantity) || 1}</p>
								<p>=</p>
								{values.isEventualPosition ? (
									<p className="font-bold">E.P.</p>
								) : (
									<p>{formatNumber(blockTotal, "currency")}</p>
								)}
							</div>
						) : (
							<div className="h-stack">
								{values.isEventualPosition ? (
									<p className="font-bold">E.P.</p>
								) : (
									<p>{formatNumber(positionsTotal, "currency")}</p>
								)}
								<p>/</p>
								<UnitTag unit={values.unit as ServiceUnit} />
							</div>
						)}
					</div>
				)}
				{isBlock ? (
					<React.Fragment>
						<NumberInput
							isRequired
							label={t("calculations.block_quantity")}
							control={methods.control}
							name={`services.${index}.quantity`}
							min={1}
							step={1}
							placeholder="1"
							isDisabled={values.isEventualPosition}
						/>
						<div className="h-stack justify-end">
							<Button
								icon={<Icon name="Plus" />}
								onClick={pickServiceModal.onOpen}
							>
								{t("calculations.add_service_to_block")}
							</Button>
						</div>
					</React.Fragment>
				) : (
					<React.Fragment>
						<ServiceFormUnitCalculatorWrapper>
							<NumberInput
								isRequired
								label={t("organization.data.unit_price")}
								control={methods.control}
								name={`services.${index}.unitPrice`}
								min={0}
								step={0.01}
								placeholder="0.00"
								data-cy="service-unit-price-input"
								variant="currency"
								errorVariant="tooltip"
								isDisabled={values.isEventualPosition}
							/>
							<div className="col-span-2">
								<UnitPicker
									options={serviceUnits}
									name={`services.${index}.unit`}
								/>
							</div>
						</ServiceFormUnitCalculatorWrapper>
						<CalculationFormItemPositions
							index={index}
							unit={values.unit as ServiceUnit}
						/>
					</React.Fragment>
				)}
				<Divider variant="content-card" />
				<div className="h-stack justify-between">
					{!isBlock ? (
						<FormCheckbox
							control={methods.control}
							name={`services.${index}.isEventualPosition`}
						>
							{t("calculations.eventual_position")}
						</FormCheckbox>
					) : (
						<div />
					)}
					<CalculationFormItemFilesAndNotes
						serviceCalculationItemId={values.id}
						notesDisclosure={notesDisclosure}
						filesDisclosure={filesDisclosure}
						containerRef={notesContainerRef}
					/>
				</div>
			</ContentCard>
			<div
				ref={notesContainerRef}
				style={{ paddingLeft: TREE_INDENT }}
			/>
			{pickServiceModal.isOpen && (
				<PickServiceModal
					isOpen={pickServiceModal.isOpen}
					onClose={pickServiceModal.onClose}
					onComplete={handleOnCompletePickService}
					onCompleteCustom={handleOnAddCustomService}
				/>
			)}
		</React.Fragment>
	);
}
